import { findNext, findPrevious, getSearchQuery, replaceAll, replaceNext } from "@codemirror/search";
import { runScopeHandlers } from "@codemirror/view";
import { ChevronDown, ChevronUp, TextAlignStart, TextSearch, X } from "lucide-react";
import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from "react";
import {
  allMatches,
  closeFind,
  findScopeOf,
  isMac,
  matchIndex,
  optionKeys,
  regexError,
  scopeCandidate,
  setQuery,
  toggleFind,
  type FindOption,
  type FindPanel,
} from "../editor/find";
import { toast } from "../lib/app";

const MOD = isMac ? "⌘" : "Ctrl+";

/** Find and replace bar along the bottom of the editor, laid out like Overleaf's, rendered into CodeMirror's search panel. */
export function FindBar({ panel, onSearchAll }: { panel: FindPanel; onSearchAll?: (text: string) => void }) {
  useSyncExternalStore(panel.subscribe, panel.getVersion);
  const { view } = panel;
  const { state } = view;
  const query = getSearchQuery(state);
  const scope = findScopeOf(state);
  const canReplace = !state.readOnly;
  const searchInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const found = useMemo(() => allMatches(state), [state.doc, query, scope]);
  const total = found?.matches.length ?? 0;
  const current = found ? matchIndex(found.matches, state.selection.main) : -1;
  const badRegex = regexError(query);
  const canScope = !!scope || scopeCandidate(view).length > 0;
  const none = !!badRegex || (!!query.search && !!found && !total);

  // Focus requests from openFind (Ctrl/⌘+F again, or opening with the replace row).
  useLayoutEffect(() => {
    const target = panel.focus;
    if (!target) return;
    panel.focus = null;
    const input = (target === "replace" && replaceInput.current) || searchInput.current;
    input?.focus();
    input?.select();
  });

  const plus = found?.capped ? "+" : "";
  const count = !query.search
    ? ""
    : badRegex
      ? "Invalid regex"
      : !total
        ? "No results"
        : current >= 0
          ? `${current + 1} of ${total}${plus}`
          : `${total}${plus} ${total === 1 ? "match" : "matches"}`;

  const doReplaceAll = () => {
    if (!total || !replaceAll(view)) return;
    toast(`Replaced ${total}${plus} ${total === 1 ? "match" : "matches"}. ${MOD}Z undoes it.`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Escape, F3, Ctrl/⌘+G, the option shortcuts and so on, from findKeymap and CodeMirror's searchKeymap.
    if (runScopeHandlers(view, e.nativeEvent, "search-panel")) {
      e.preventDefault();
      return;
    }
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (e.target === replaceInput.current) {
      if (e.metaKey || e.ctrlKey) doReplaceAll();
      else replaceNext(view);
    } else (e.shiftKey ? findPrevious : findNext)(view);
  };

  const toggle = (option: FindOption, on: boolean, title: string, label: React.ReactNode, className = "", disabled = false) => (
    <button
      type="button"
      className={`find-toggle${className}${on ? " on" : ""}`}
      title={disabled ? "Select some text first to search in it" : `${title} (${optionKeys[option]})`}
      aria-label={title}
      aria-pressed={on}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => toggleFind(view, option)}
    >
      {label}
    </button>
  );

  return (
    <div className="find-bar" role="search" onKeyDown={onKeyDown}>
      <div className="find-row">
        <div className={`find-input${none ? " none" : ""}`}>
          <input
            ref={searchInput}
            type="text"
            placeholder={scope ? "Search in selection" : "Search for"}
            aria-label="Search for"
            title={badRegex ?? `Enter: next · Shift+Enter: previous · Alt+Enter: select all matches`}
            spellCheck={false}
            autoComplete="off"
            value={query.search}
            onChange={(e) => {
              setQuery(view, { search: e.target.value });
              panel.findFromAnchor();
            }}
          />
          {toggle("caseSensitive", query.caseSensitive, "Match case", "Aa")}
          {toggle("regexp", query.regexp, "Regular expression", "[.*]", " mono")}
          {toggle("wholeWord", query.wholeWord, "Whole words", "W", " bold")}
          {toggle("inSelection", !!scope, "Search in selection", <TextAlignStart size={15} />, "", !canScope)}
        </div>
        <div className="find-group">
          <button type="button" className="icon-btn" title="Previous match (Shift+Enter)" aria-label="Previous match" disabled={!total} onClick={() => findPrevious(view)}>
            <ChevronUp size={16} />
          </button>
          <button type="button" className="icon-btn" title="Next match (Enter)" aria-label="Next match" disabled={!total} onClick={() => findNext(view)}>
            <ChevronDown size={16} />
          </button>
        </div>
        {onSearchAll && (
          <button
            type="button"
            className="icon-btn find-pill"
            title={`Search all files (${isMac ? "⇧⌘F" : "Ctrl+Shift+F"})`}
            aria-label="Search all files"
            onClick={() => onSearchAll(query.search)}
          >
            <TextSearch size={16} />
          </button>
        )}
        <span className={`find-count${none ? " none" : ""}`} aria-live="polite" title={badRegex ?? undefined}>
          {count}
        </span>
        <button type="button" className="icon-btn find-close" title="Close (Esc)" aria-label="Close" onClick={() => closeFind(view)}>
          <X size={16} />
        </button>
      </div>
      {canReplace && (
        <div className="find-row">
          <div className="find-input">
            <input
              ref={replaceInput}
              type="text"
              placeholder={query.regexp ? "Replace with ($1 for groups)" : "Replace with"}
              aria-label="Replace with"
              spellCheck={false}
              autoComplete="off"
              value={query.replace}
              onChange={(e) => setQuery(view, { replace: e.target.value })}
            />
          </div>
          <button type="button" className="find-btn" title="Replace this match and move to the next (Enter)" disabled={!total} onClick={() => replaceNext(view)}>
            Replace
          </button>
          <button type="button" className="find-btn" title={`Replace every match (${MOD}Enter)`} disabled={!total} onClick={doReplaceAll}>
            Replace All
          </button>
        </div>
      )}
    </div>
  );
}
